import { z } from "zod";
/** Stable machine key: never renamed (CLAUDE.md rule 6). */
export const Key = z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z][a-z0-9_]*$/, "key must be lower-case letters, digits and underscores, starting with a letter");
export const OptionKey = z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9][a-z0-9_-]*$/, "option key must be lower-case letters, digits, underscores or hyphens");
export const EstimateUnit = z.enum(["none", "hours", "days", "points", "tshirt"]);
export const StateCategory = z.enum(["todo", "in_progress", "done", "cancelled"]);
export const FieldKind = z.enum([
    "text",
    "rich_text",
    "integer",
    "decimal",
    "boolean",
    "date",
    "datetime",
    "single_select",
    "multi_select",
    "user",
    "object_ref",
    "url",
]);
export const DefinitionStatus = z.enum(["active", "archived"]);
export const FieldStatus = z.enum(["active", "hidden"]);
const Id = z.number().int().positive();
const Label = z.string().min(1).max(200);
const Colour = z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .nullable();
/** Kind-specific settings. Validated per kind in core. */
export const FieldConfig = z
    .object({
    min: z.number().optional(),
    max: z.number().optional(),
    maxLength: z.number().int().positive().optional(),
    /** user / object_ref: allow more than one value */
    allowMultiple: z.boolean().optional(),
    /** object_ref: restrict to these type keys */
    allowedTypes: z.array(Key).optional(),
})
    .strict();
// ---------- entities ----------
export const WorkflowDefinition = z.object({
    id: Id,
    key: Key,
    name: Label,
    description: z.string(),
    status: DefinitionStatus,
    copiedFromId: Id.nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const State = z.object({
    id: Id,
    typeId: Id,
    key: Key,
    name: Label,
    description: z.string(),
    colour: Colour,
    category: StateCategory,
    isInitial: z.boolean(),
    sort: z.number().int(),
});
export const Transition = z.object({ fromStateId: Id, toStateId: Id });
export const ObjectType = z.object({
    id: Id,
    definitionId: Id,
    key: Key,
    name: Label,
    plural: Label,
    description: z.string(),
    estimateUnit: EstimateUnit,
    enforceTransitions: z.boolean(),
    sort: z.number().int(),
    /** the timebox level this type's objects are allocated at; null = never (design 3.7) */
    timeboxLevelId: Id.nullable(),
});
/** A length of time: `{count: 2, unit: "week"}`. */
export const TimeboxLength = z.object({ count: z.number().int().min(1).max(366), unit: z.enum(["day", "week", "month"]) }).strict();
/** One level of time period in a definition, e.g. Release above Sprint (design 3.7). */
export const TimeboxLevel = z.object({
    id: Id,
    definitionId: Id,
    key: Key,
    label: Label,
    plural: Label,
    /** 0 = top; each level's periods sit inside a period of the level above */
    sort: z.number().int(),
    defaultLength: TimeboxLength.nullable(),
    carryOver: z.boolean(),
});
export const ContainmentRule = z.object({
    /** null = allowed at the container root */
    parentTypeId: Id.nullable(),
    childTypeId: Id,
});
export const FieldOption = z.object({
    id: Id,
    fieldId: Id,
    key: OptionKey,
    label: Label,
    colour: Colour,
    sort: z.number().int(),
    archived: z.boolean(),
});
export const FieldDefinition = z.object({
    id: Id,
    definitionId: Id,
    key: Key,
    label: Label,
    description: z.string(),
    kind: FieldKind,
    required: z.boolean(),
    defaultValue: z.unknown().nullable(),
    config: FieldConfig,
    status: FieldStatus,
    templateKey: z.string().nullable(),
    sort: z.number().int(),
    options: z.array(FieldOption),
    typeIds: z.array(Id),
});
/** A type with its states and transitions, as `describe` returns it. */
export const ObjectTypeDetail = ObjectType.extend({
    states: z.array(State),
    transitions: z.array(Transition),
    fieldIds: z.array(Id),
});
export const DefinitionDetail = WorkflowDefinition.extend({
    types: z.array(ObjectTypeDetail),
    containment: z.array(ContainmentRule),
    fields: z.array(FieldDefinition),
    timeboxLevels: z.array(TimeboxLevel),
});
/** The whole workspace vocabulary in one call (design 6.4). Containers join in step 8. */
export const Describe = z.object({
    workspace: z.object({ id: z.uuid(), slug: z.string(), name: z.string() }),
    definitions: z.array(DefinitionDetail),
    fieldTemplates: z.array(z.lazy(() => FieldTemplate)),
    etag: z.string(),
});
// ---------- inputs ----------
export const CreateDefinitionInput = z.object({
    key: Key,
    name: Label,
    description: z.string().max(5000).default(""),
});
export const UpdateDefinitionInput = z
    .object({
    name: Label.optional(),
    description: z.string().max(5000).optional(),
    status: DefinitionStatus.optional(),
})
    .strict();
export const CreateTypeInput = z.object({
    key: Key,
    name: Label,
    plural: Label.optional(),
    description: z.string().max(5000).default(""),
    estimateUnit: EstimateUnit.default("none"),
    enforceTransitions: z.boolean().default(false),
    sort: z.number().int().optional(),
});
export const UpdateTypeInput = z
    .object({
    name: Label.optional(),
    plural: Label.optional(),
    description: z.string().max(5000).optional(),
    estimateUnit: EstimateUnit.optional(),
    enforceTransitions: z.boolean().optional(),
    sort: z.number().int().optional(),
    timeboxLevelId: Id.nullable().optional(),
    /** when the type's level changes while objects are allocated: clear them, or (moving up) re-point at the parent period */
    allocations: z.enum(["clear", "parent"]).optional(),
})
    .strict();
/** New levels go at the bottom unless `sort` puts them elsewhere (refused once periods exist that would re-nest). */
export const CreateTimeboxLevelInput = z.object({
    key: Key,
    label: Label,
    plural: Label.optional(),
    sort: z.number().int().min(0).optional(),
    defaultLength: TimeboxLength.nullable().optional(),
    carryOver: z.boolean().default(false),
});
export const UpdateTimeboxLevelInput = z
    .object({ label: Label.optional(), plural: Label.optional(), defaultLength: TimeboxLength.nullable().optional(), carryOver: z.boolean().optional() })
    .strict();
export const CreateStateInput = z.object({
    key: Key,
    name: Label,
    description: z.string().max(2000).default(""),
    colour: Colour.optional(),
    category: StateCategory,
    isInitial: z.boolean().default(false),
    sort: z.number().int().optional(),
});
export const UpdateStateInput = z
    .object({
    name: Label.optional(),
    description: z.string().max(2000).optional(),
    colour: Colour.optional(),
    category: StateCategory.optional(),
    isInitial: z.literal(true).optional(),
    sort: z.number().int().optional(),
})
    .strict();
/** Removing an occupied state needs a target; the API passes it as a query parameter. */
export const RemoveStateQuery = z.object({ target_state_id: z.coerce.number().int().positive().optional() });
export const SetTransitionsInput = z.object({ transitions: z.array(Transition) });
export const ContainmentRuleInput = ContainmentRule;
export const CreateFieldInput = z.object({
    key: Key,
    label: Label,
    description: z.string().max(2000).default(""),
    kind: FieldKind,
    required: z.boolean().default(false),
    defaultValue: z.unknown().optional(),
    config: FieldConfig.default({}),
    sort: z.number().int().optional(),
    options: z
        .array(z.object({ key: OptionKey, label: Label, colour: Colour.optional(), sort: z.number().int().optional() }))
        .optional(),
    typeIds: z.array(Id).default([]),
    /** record that this came from a template */
    templateKey: z.string().optional(),
});
export const UpdateFieldInput = z
    .object({
    label: Label.optional(),
    description: z.string().max(2000).optional(),
    /** making a field required needs a default to back-fill unless no bound object lacks a value */
    required: z.boolean().optional(),
    defaultValue: z.unknown().optional(),
    config: FieldConfig.optional(),
    status: FieldStatus.optional(),
    sort: z.number().int().optional(),
})
    .strict();
export const CreateOptionInput = z.object({ key: OptionKey, label: Label, colour: Colour.optional(), sort: z.number().int().optional() });
export const UpdateOptionInput = z
    .object({ label: Label.optional(), colour: Colour.optional(), sort: z.number().int().optional(), archived: z.boolean().optional() })
    .strict();
export const SetBindingsInput = z.object({ typeIds: z.array(Id) });
/** Pre-defined fields a workflow admin can attach in one call (PRD 5.4). */
export const FieldTemplate = z.object({
    key: z.string(),
    label: Label,
    description: z.string(),
    kind: FieldKind,
    config: FieldConfig,
    options: z.array(z.object({ key: OptionKey, label: Label, colour: Colour.optional() })).optional(),
});
// ---------- export / import ----------
/** Portable, id-free definition. Keys carry identity. */
export const DefinitionExport = z.object({
    format: z.literal("workcore.definition/1"),
    key: Key,
    name: Label,
    description: z.string().default(""),
    types: z.array(z.object({
        key: Key,
        name: Label,
        plural: Label,
        description: z.string().default(""),
        estimateUnit: EstimateUnit.default("none"),
        enforceTransitions: z.boolean().default(false),
        states: z.array(z.object({
            key: Key,
            name: Label,
            description: z.string().default(""),
            colour: Colour.optional(),
            category: StateCategory,
            isInitial: z.boolean().default(false),
        })),
        /** pairs of state keys */
        transitions: z.array(z.tuple([Key, Key])).default([]),
        /** key of the timebox level the type is allocated at */
        timeboxLevel: Key.optional(),
    })),
    /** top to bottom */
    timeboxLevels: z
        .array(z.object({ key: Key, label: Label, plural: Label, defaultLength: TimeboxLength.nullable().default(null), carryOver: z.boolean().default(false) }))
        .default([]),
    /** [parentTypeKey|null, childTypeKey] */
    containment: z.array(z.tuple([Key.nullable(), Key])),
    fields: z
        .array(z.object({
        key: Key,
        label: Label,
        description: z.string().default(""),
        kind: FieldKind,
        required: z.boolean().default(false),
        defaultValue: z.unknown().optional(),
        config: FieldConfig.default({}),
        options: z.array(z.object({ key: OptionKey, label: Label, colour: Colour.optional(), archived: z.boolean().default(false) })).default([]),
        types: z.array(Key),
        templateKey: z.string().optional(),
    }))
        .default([]),
});
export const ImportDefinitionInput = z.object({
    definition: DefinitionExport,
    /** override the key inside the export (e.g. when copying) */
    key: Key.optional(),
    name: Label.optional(),
});
export const CopyDefinitionInput = z.object({ key: Key, name: Label });
export const CopyTypeInput = z.object({ targetDefinitionId: Id, key: Key.optional() });
/** How much data each part of a definition holds (`GET /definitions/:id/usage`). */
export const DefinitionUsage = z.object({
    types: z.array(z.object({ typeId: Id, objects: z.number().int(), includingDeleted: z.number().int() })),
    states: z.array(z.object({ stateId: Id, objects: z.number().int() })),
    fields: z.array(z.object({ fieldId: Id, withValue: z.number().int(), missing: z.number().int() })),
});
//# sourceMappingURL=definitions.js.map